import { memo, useCallback, useEffect } from "react";
import { Message } from "@/components/messages/Message";
import { useMessageData } from "@/lib/selectors/messages";
import type { MessageEntrySource } from "@/lib/react-message-view";
import type { RenderUser } from "@/lib/render-user";
import { csCountRender } from "@/lib/channel-switch-profile";
import { scrollRowMounted, scrollRowUnmounted } from "@/lib/scroll-profile";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyArray = readonly any[];

export interface MessageRowProps {
  messageId: string;
  channelId: string;
  compact: boolean;
  highlighted: boolean;
  isPinned: boolean;
  activeCall: boolean;
  entrySource: MessageEntrySource;
  renderUserMap: Map<string, RenderUser>;
  workspaceMembers: { id: string; display_name: string }[];
  workspaceChannels: { id: string; name: string }[];
  /** Active workspace-member user IDs. `null` for DM channels. */
  activeWorkspaceMemberIds: Set<string> | null;
  // Child data from parent-level Maps (selectors, not per-row subs)
  reactions: AnyArray | undefined;
  /** Community-curation marks (helpful / worth_reading / best_answer). */
  signals: AnyArray | undefined;
  attachments: AnyArray | undefined;
  linkPreviews: AnyArray | undefined;
  /** True when this message is the source of a thread (i.e. someone
   *  has clicked "Create Thread" on it). Drives the side-bar accent on
   *  the row + the "Thread • N messages" badge under the body. Computed
   *  upstream from `threadSourceIds.has(messageId)` so it's a primitive
   *  bool and only the row whose state flips re-renders. */
  threadActive: boolean;
  onThreadClick: (id: string | null) => void;
  /** Discord-shape "Reply" hover action. Sets `replyingToMessageId` in
   *  MessageArea state so the next composer send goes via `addReply`. */
  onInlineReply?: (messageId: string) => void;
  /** Discord-shape "Create Thread" hover action. Fires the
   *  `actions.createThread` mutator which posts a system message in
   *  the channel + opens the sidebar at the source message. */
  onCreateThread?: (messageId: string) => void;
  /** Inline-reply citation pill click — scrolls the channel timeline
   *  to the cited source message in-place. */
  onScrollToMessage?: (messageId: string) => void;
  onPin: (id: string) => void;
  onUserClick: (id: string) => void;
  onSendToShell?: (messageId: string) => void;
  onChannelClick: (channelId: string) => void;
  onJoinCall?: (mode: "audio" | "video") => void;
  actionPillOpen: boolean;
  setMessageOpen: (id: string, open: boolean) => void;
}

export const MessageRow = memo(function MessageRow({
  messageId,
  channelId,
  compact,
  highlighted,
  isPinned,
  activeCall,
  entrySource,
  renderUserMap,
  workspaceMembers,
  workspaceChannels,
  activeWorkspaceMemberIds,
  reactions,
  signals,
  attachments,
  linkPreviews,
  threadActive,
  onThreadClick,
  onInlineReply,
  onCreateThread,
  onScrollToMessage,
  onPin,
  onUserClick,
  onSendToShell,
  onChannelClick,
  onJoinCall,
  actionPillOpen,
  setMessageOpen,
}: MessageRowProps) {
  csCountRender("MessageRow");

  useEffect(() => {
    scrollRowMounted();
    return scrollRowUnmounted;
  }, []);

  const msg = useMessageData(entrySource, messageId);
  // mig 130 — when this row is an inline reply, resolve the source via
  // the same per-message subscription. Empty-string fallback keeps the
  // hook unconditional (React rules); the underlying view returns
  // undefined for the unknown id with negligible cost.
  const quotedSource = useMessageData(
    entrySource,
    msg?.replied_to_message_id ?? "",
  );
  const handlePin = useCallback(() => onPin(messageId), [onPin, messageId]);
  const handleActionPillChange = useCallback(
    (open: boolean) => setMessageOpen(messageId, open),
    [setMessageOpen, messageId],
  );

  if (!msg) return null;

  const user = renderUserMap.get(msg.user_id) ?? null;
  const quotedSourceUser = quotedSource
    ? (renderUserMap.get(quotedSource.user_id) ?? null)
    : null;
  const quotedSourceAuthorName = quotedSourceUser?.display_name ?? null;
  const quotedSourceAuthorAvatarURL = quotedSourceUser?.avatar_url ?? null;
  // Former member: the sender is not in the active workspace-members
  // set. Only applies to workspace channels (activeWorkspaceMemberIds is
  // null for DMs). Deactivated wins visually — we pass both and let the
  // Message component apply precedence.
  const senderIsFormerMember =
    !!activeWorkspaceMemberIds && !activeWorkspaceMemberIds.has(msg.user_id);

  return (
    <Message
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      message={msg as any}
      user={user}
      reactions={reactions}
      signals={signals}
      linkPreviews={linkPreviews}
      attachments={attachments}
      userMap={renderUserMap}
      workspaceMembers={workspaceMembers}
      compact={compact}
      onThreadClick={onThreadClick}
      onInlineReply={onInlineReply}
      onCreateThread={onCreateThread}
      onScrollToMessage={onScrollToMessage}
      quotedSource={quotedSource ?? null}
      quotedSourceAuthorName={quotedSourceAuthorName}
      quotedSourceAuthorAvatarURL={quotedSourceAuthorAvatarURL}
      threadReplyCount={msg.reply_count ?? 0}
      threadReplyUsers={msg.reply_users as string[] | null}
      threadLatestReplyAt={msg.latest_reply_at}
      threadActive={threadActive}
      highlighted={highlighted}
      isPinned={isPinned}
      onPin={handlePin}
      onUserClick={onUserClick}
      actionPillOpen={actionPillOpen}
      onActionPillOpenChange={handleActionPillChange}
      onJoinCall={activeCall ? onJoinCall : undefined}
      onSendToShell={onSendToShell}
      channelId={channelId}
      channels={workspaceChannels}
      onChannelClick={onChannelClick}
      senderIsFormerMember={senderIsFormerMember}
    />
  );
});
